import React, { useState } from "react";
import {
  Users,
  MessageSquare,
  Calendar,
  Activity,
  TrendingUp,
  ArrowUp,
  ArrowDown,
  Minus,
  Clock,
  Star,
  BookOpen,
  Award,
} from "lucide-react";
import GuidelinesModal from "./GuidelinesModal";
import "./styles/index.css";

const CommunityStats = ({ community, analytics, previewData }) => {
  // Use real analytics data if available, fallback to mock data
  const analyticsData = analytics || previewData?.analytics;

  // 🆕 NEW: Guidelines modal state
  const [showGuidelinesModal, setShowGuidelinesModal] = useState(false);

  const stats = [
    {
      label: "Total Members",
      value: analyticsData?.totalMembers || community?.memberCount || "0",
      subtext:
        analyticsData?.memberGrowth?.description || "No growth data available",
      icon: Users,
      color: "#2196f3",
      trend: "positive",
    },
    {
      label: "Active Discussions",
      value: analyticsData?.activeDiscussions || "0",
      subtext: "Currently ongoing",
      icon: MessageSquare,
      color: "#4caf50",
      trend: "positive",
    },
    {
      label: "Research Rooms",
      value: community?.roomCount || previewData?.rooms?.total || "0",
      subtext: "Research collaboration spaces",
      icon: BookOpen,
      color: "#ff9800",
      trend: "neutral",
    },
    {
      label: "Community Rating",
      value: previewData?.rating?.summary?.averageOverall?.toFixed(1) || "0.0",
      subtext: `Based on ${
        previewData?.rating?.summary?.totalRatings || 0
      } reviews`,
      icon: Star,
      color: "#ffc107",
      trend: "positive",
    },
  ];

  const insights = [
    {
      title: "Member Engagement",
      value: analyticsData?.memberEngagement?.change || "0%",
      subtitle:
        analyticsData?.memberEngagement?.description ||
        "No engagement data available",
      icon: TrendingUp,
      trend: analyticsData?.memberEngagement?.change?.includes("+")
        ? "positive"
        : "neutral",
    },
    {
      title: "Daily Active Users",
      value: analyticsData?.dailyActiveUsers?.count?.toString() || "0",
      subtitle:
        analyticsData?.dailyActiveUsers?.description ||
        "No active user data available",
      icon: Activity,
      trend: "positive",
    },
    {
      title: "New Posts Today",
      value: analyticsData?.newPostsToday?.count?.toString() || "0",
      subtitle:
        analyticsData?.newPostsToday?.description || "No post data available",
      icon: Clock,
      trend: "neutral",
    },
    {
      title: "Community Growth",
      value: analyticsData?.memberGrowth?.change || "0%",
      subtitle:
        analyticsData?.memberGrowth?.description || "No growth data available",
      icon: Award,
      trend: analyticsData?.memberGrowth?.change?.includes("+")
        ? "positive"
        : "neutral",
    },
  ];

  // Get guidelines from preview data - NO MORE FAKE FALLBACK DATA
  const guidelines = previewData?.guidelines?.guidelines || [];

  return (
    <>
      <div className="community-preview-stats">
        <div className="community-preview-content-card">
          <h3>
            <Activity size={20} />
            Community Overview
          </h3>

          <div className="community-preview-stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="community-preview-stat-card">
                <div
                  className="community-preview-stat-icon"
                  style={{
                    backgroundColor: `${stat.color}15`,
                    color: stat.color,
                  }}
                >
                  <stat.icon size={24} />
                </div>
                <div className="community-preview-stat-content">
                  <div className="community-preview-stat-value">
                    {stat.value}
                  </div>
                  <div className="community-preview-stat-label">
                    {stat.label}
                  </div>
                  <div className="community-preview-stat-subtext">
                    {stat.subtext}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="community-preview-content-card">
          <h3>
            <TrendingUp size={20} />
            Community Insights
          </h3>

          <div className="community-preview-insights-list">
            {insights.map((insight, index) => (
              <div key={index} className="community-preview-insight-item">
                <div className="community-preview-insight-icon">
                  <insight.icon size={18} />
                </div>
                <div className="community-preview-insight-content">
                  <div className="community-preview-insight-header">
                    <span className="community-preview-insight-title">
                      {insight.title}
                    </span>
                    <span
                      className={`community-preview-insight-value ${insight.trend}`}
                    >
                      {insight.value}
                    </span>
                  </div>
                  <div className="community-preview-insight-subtitle">
                    {insight.subtitle}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="community-preview-content-card">
          <h3>
            <BookOpen size={20} />
            Community Guidelines
          </h3>

          {guidelines.length > 0 ? (
            <>
              <div className="community-preview-guidelines-preview">
                {guidelines.slice(0, 3).map((guideline, index) => (
                  <div key={index} className="community-preview-guideline-item">
                    <div className="community-preview-guideline-number">
                      {guideline.order || index + 1}
                    </div>
                    <div className="community-preview-guideline-text">
                      <strong>{guideline.title}</strong>
                      <p>{guideline.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {guidelines.length > 3 && (
                <button
                  className="community-preview-view-all-guidelines"
                  onClick={() => setShowGuidelinesModal(true)}
                >
                  View All Guidelines ({previewData?.guidelines?.totalGuidelines || guidelines.length})
                </button>
              )}
            </>
          ) : (
            <div className="community-preview-no-guidelines">
              <BookOpen size={24} style={{ opacity: 0.4, marginBottom: 8 }} />
              <p style={{ color: '#9e9e9e', margin: 0 }}>No community guidelines set yet.</p>
            </div>
          )}
        </div>
      </div>

      {/* 🆕 NEW: Guidelines Modal */}
      <GuidelinesModal
        isOpen={showGuidelinesModal}
        onClose={() => setShowGuidelinesModal(false)}
        guidelines={guidelines}
        communityName={community?.name}
      />
    </>
  );
};

export default CommunityStats;
